/**
 * MediaProcessor Service
 * Client-side video/audio processing for large files
 * Extracts audio and frames without server upload
 */

export interface ProcessingProgress {
  stage: 'loading' | 'extracting-audio' | 'extracting-frames' | 'compressing' | 'complete';
  percent: number;
  message: string;
}

export interface ExtractedMedia {
  audioBlob: Blob;
  audioUrl: string;
  frames: ExtractedFrame[];
  duration: number;
  originalSize: number;
  processedSize: number;
}

export interface ExtractedFrame {
  timestamp: number;
  blob: Blob;
  dataUrl: string;
}

export class MediaProcessor {
  private audioContext: AudioContext | null = null;
  private onProgress: ((progress: ProcessingProgress) => void) | null = null;

  /**
   * Set progress callback
   */
  setProgressCallback(callback: (progress: ProcessingProgress) => void): void {
    this.onProgress = callback;
  }

  /**
   * Report progress
   */
  private reportProgress(stage: ProcessingProgress['stage'], percent: number, message: string): void {
    this.onProgress?.({ stage, percent, message });
  }

  /**
   * Process a large video file - extract audio and key frames
   */
  async processVideoFile(
    file: File,
    frameTimestamps?: number[]
  ): Promise<ExtractedMedia> {
    this.reportProgress('loading', 0, 'Loading video file...');

    // Create video element
    const video = document.createElement('video');
    video.muted = true;
    video.playsInline = true;

    // Load video
    const videoUrl = URL.createObjectURL(file);
    video.src = videoUrl;

    await new Promise<void>((resolve, reject) => {
      video.onloadedmetadata = () => resolve();
      video.onerror = () => reject(new Error('Failed to load video file'));
    });

    const duration = video.duration;
    this.reportProgress('loading', 10, `Video loaded: ${this.formatDuration(duration)}`);

    // Extract audio
    this.reportProgress('extracting-audio', 15, 'Extracting audio track...');
    const audioBlob = await this.extractAudioFromVideo(file, (percent) => {
      this.reportProgress('extracting-audio', 15 + percent * 0.5, 'Extracting audio track...');
    });

    // Determine frame timestamps if not provided
    // Extract frames at: start, every 30 seconds, and key speaker change points
    const timestamps = frameTimestamps || this.generateFrameTimestamps(duration);

    // Extract frames
    this.reportProgress('extracting-frames', 70, 'Capturing video frames to read speaker names...');
    const frames = await this.extractFrames(video, timestamps, (percent) => {
      this.reportProgress('extracting-frames', 70 + percent * 0.25, 'Capturing video frames...');
    });

    // Cleanup
    URL.revokeObjectURL(videoUrl);

    const audioUrl = URL.createObjectURL(audioBlob);

    this.reportProgress('complete', 100, 'Media processing complete');

    return {
      audioBlob,
      audioUrl,
      frames,
      duration,
      originalSize: file.size,
      processedSize: audioBlob.size,
    };
  }

  /**
   * Extract audio from video using Web Audio API
   */
  private async extractAudioFromVideo(
    file: File,
    onProgress?: (percent: number) => void
  ): Promise<Blob> {
    // Initialize audio context
    if (!this.audioContext) {
      this.audioContext = new AudioContext();
    }

    onProgress?.(0.1);

    // Decode the entire file
    const arrayBuffer = await file.arrayBuffer();
    onProgress?.(0.3);

    const audioBuffer = await this.audioContext.decodeAudioData(arrayBuffer);
    onProgress?.(0.6);

    // Convert to WAV
    const wavBlob = this.audioBufferToWav(audioBuffer);
    onProgress?.(1);

    return wavBlob;
  }

  /**
   * Convert AudioBuffer to WAV Blob
   */
  private audioBufferToWav(buffer: AudioBuffer): Blob {
    const numChannels = 1; // Mono — speech transcription doesn't need stereo
    const sampleRate = Math.min(buffer.sampleRate, 16000); // Downsample to 16kHz for speech
    const format = 1; // PCM
    const bitDepth = 16;

    // Resample if needed
    const resampledBuffer = this.resampleBuffer(buffer, sampleRate);
    
    const bytesPerSample = bitDepth / 8;
    const blockAlign = numChannels * bytesPerSample;
    const dataLength = resampledBuffer.length * numChannels * bytesPerSample;
    const headerLength = 44;
    const totalLength = headerLength + dataLength;

    const arrayBuffer = new ArrayBuffer(totalLength);
    const view = new DataView(arrayBuffer);

    // WAV header
    this.writeString(view, 0, 'RIFF');
    view.setUint32(4, totalLength - 8, true);
    this.writeString(view, 8, 'WAVE');
    this.writeString(view, 12, 'fmt ');
    view.setUint32(16, 16, true); // fmt chunk size
    view.setUint16(20, format, true);
    view.setUint16(22, numChannels, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, sampleRate * blockAlign, true);
    view.setUint16(32, blockAlign, true);
    view.setUint16(34, bitDepth, true);
    this.writeString(view, 36, 'data');
    view.setUint32(40, dataLength, true);

    // Write audio data
    const offset = 44;
    const channelData: Float32Array[] = [];
    for (let ch = 0; ch < numChannels; ch++) {
      channelData.push(resampledBuffer.getChannelData(ch));
    }

    for (let i = 0; i < resampledBuffer.length; i++) {
      for (let ch = 0; ch < numChannels; ch++) {
        const sample = Math.max(-1, Math.min(1, channelData[ch][i]));
        const intSample = sample < 0 ? sample * 0x8000 : sample * 0x7FFF;
        view.setInt16(offset + (i * numChannels + ch) * bytesPerSample, intSample, true);
      }
    }

    return new Blob([arrayBuffer], { type: 'audio/wav' });
  }

  /**
   * Resample audio buffer to target sample rate
   */
  private resampleBuffer(buffer: AudioBuffer, targetSampleRate: number): AudioBuffer {
    if (buffer.sampleRate === targetSampleRate) {
      return buffer;
    }

    const ratio = buffer.sampleRate / targetSampleRate;
    const newLength = Math.round(buffer.length / ratio);
    
    // Linear interpolation resampling (synchronous — no async OfflineAudioContext render needed)
    const numChannels = Math.min(buffer.numberOfChannels, 2);
    const ctx = new OfflineAudioContext(numChannels, newLength, targetSampleRate);
    const newBuffer = ctx.createBuffer(numChannels, newLength, targetSampleRate);

    for (let ch = 0; ch < numChannels; ch++) {
      const oldData = buffer.getChannelData(ch);
      const newData = newBuffer.getChannelData(ch);

      for (let i = 0; i < newLength; i++) {
        const srcIdx = i * ratio;
        const srcIdxFloor = Math.floor(srcIdx);
        const srcIdxCeil = Math.min(srcIdxFloor + 1, oldData.length - 1);
        const t = srcIdx - srcIdxFloor;
        newData[i] = oldData[srcIdxFloor] * (1 - t) + oldData[srcIdxCeil] * t;
      }
    }

    return newBuffer;
  }

  /**
   * Write string to DataView
   */
  private writeString(view: DataView, offset: number, str: string): void {
    for (let i = 0; i < str.length; i++) {
      view.setUint8(offset + i, str.charCodeAt(i));
    }
  }

  /**
   * Generate frame timestamps for speaker identification
   * Enhanced: More frequent captures in first 2 minutes where names typically appear
   * Pattern: Every 5s for first 60s, every 10s for 60-120s, then every 30s
   */
  private generateFrameTimestamps(duration: number): number[] {
    const timestamps: number[] = [0]; // Always capture start
    
    // First 60 seconds: every 5 seconds (high density for name overlays)
    for (let t = 5; t <= Math.min(60, duration); t += 5) {
      timestamps.push(t);
    }
    
    // 60-120 seconds: every 10 seconds
    for (let t = 70; t <= Math.min(120, duration); t += 10) {
      timestamps.push(t);
    }
    
    // After 120 seconds: every 30 seconds
    for (let t = 150; t < duration; t += 30) {
      timestamps.push(t);
    }

    // Add a frame near the end
    if (duration > 10 && timestamps[timestamps.length - 1] < duration - 5) {
      timestamps.push(duration - 2);
    }

    // Limit to max 30 frames (increased from 20 due to denser early sampling)
    if (timestamps.length > 30) {
      const step = Math.ceil(timestamps.length / 30);
      return timestamps.filter((_, i) => i % step === 0 || i === timestamps.length - 1);
    }

    return timestamps;
  }

  /**
   * Extract frames from video at specified timestamps
   */
  private async extractFrames(
    video: HTMLVideoElement,
    timestamps: number[],
    onProgress?: (percent: number) => void
  ): Promise<ExtractedFrame[]> {
    const frames: ExtractedFrame[] = [];
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      throw new Error('Could not create canvas context');
    }

    // Set canvas size to reasonable resolution for OCR
    const maxWidth = 1280;
    const maxHeight = 720;
    const scale = Math.min(maxWidth / video.videoWidth, maxHeight / video.videoHeight, 1);
    canvas.width = video.videoWidth * scale;
    canvas.height = video.videoHeight * scale;

    for (let i = 0; i < timestamps.length; i++) {
      const timestamp = timestamps[i];
      
      try {
        // Seek to timestamp
        video.currentTime = timestamp;
        await new Promise<void>((resolve) => {
          video.onseeked = () => resolve();
        });

        // Draw frame to canvas
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

        // Convert to blob
        const blob = await new Promise<Blob>((resolve, reject) => {
          canvas.toBlob(
            (b) => (b ? resolve(b) : reject(new Error('Failed to capture frame'))),
            'image/jpeg',
            0.85
          );
        });

        const dataUrl = canvas.toDataURL('image/jpeg', 0.85);

        frames.push({
          timestamp,
          blob,
          dataUrl,
        });
      } catch (err) {
        console.warn(`Failed to extract frame at ${timestamp}s:`, err);
      }

      onProgress?.((i + 1) / timestamps.length);
    }

    return frames;
  }

  /**
   * Format duration as MM:SS or HH:MM:SS
   */
  private formatDuration(seconds: number): string {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor(seconds % 60);

    if (h > 0) {
      return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
    }
    return `${m}:${s.toString().padStart(2, '0')}`;
  }

  /**
   * Check if file needs preprocessing (large video)
   */
  static needsPreprocessing(file: File): boolean {
    const maxDirectSize = 100 * 1024 * 1024; // 100MB
    const isVideo = file.type.startsWith('video/') || 
      /\.(mp4|webm|mov|avi|mkv)$/i.test(file.name);
    
    return isVideo && file.size > maxDirectSize;
  }

  /**
   * Get estimated processing time
   */
  static getEstimatedTime(fileSize: number): string {
    // Rough estimate: ~1 minute per 100MB
    const minutes = Math.ceil(fileSize / (100 * 1024 * 1024));
    if (minutes < 1) return 'less than a minute';
    if (minutes === 1) return 'about 1 minute';
    return `about ${minutes} minutes`;
  }

  /**
   * Cleanup resources
   */
  cleanup(): void {
    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
    }
  }
}

// Singleton instance
export const mediaProcessor = new MediaProcessor();
